import { useMemo } from 'react';
import { useQuery } from 'urql';

import { gql } from '@/shared/types/gql';

/**
 * The tenant's per-section grant, as the backend's `RoleAccessLevel` enum.
 * `FULL_ACCESS` > `READ_ONLY` > `NO_ACCESS`.
 */
export type SectionAccessLevel = 'NO_ACCESS' | 'READ_ONLY' | 'FULL_ACCESS';

/**
 * The caller's own section→level map, straight from the same resolved
 * permission map `PermissionGuard` consults for `@RequirePermission`
 * decisions — so a control this hook enables is a control the server will
 * actually accept.
 *
 * This is the runtime answer to `usePermissions().can()`, which reads
 * `user.role.permissions` — a field the API's `User` does not have, so it
 * always denied and every caller had to route around it.
 */
export const CURRENT_USER_SECTION_ACCESS = gql`
  query CurrentUserSectionAccess {
    currentUserPermissions {
      isSuperAdmin
      permissions {
        section
        level
      }
    }
  }
`;

export interface CurrentUserPermissionsData {
  currentUserPermissions?: {
    isSuperAdmin?: boolean | null;
    permissions?: ({ section?: string | null; level?: string | null } | null)[] | null;
  } | null;
}

export interface SectionAccess {
  /** `null` until the server answers. */
  level: SectionAccessLevel | null;
  /** `READ_ONLY` or `FULL_ACCESS` — the section may be opened at all. */
  canRead: boolean;
  /** `FULL_ACCESS` — the level every write endpoint behind the section requires. */
  canWrite: boolean;
  fetching: boolean;
}

/**
 * Level for one section out of a `currentUserPermissions` payload, or `null`
 * while the server has not answered.
 *
 * A FAILED query resolves to `FULL_ACCESS`, matching `useModuleFlags`'
 * fail-open precedent: guessing "read-only" off a dropped request would strand
 * a full-access user with every button greyed out, which reads as a broken app,
 * and enforcement is server-side regardless.
 *
 * An UNANSWERED query is `null`, which is not the same thing — see
 * `useSectionAccess` for why writes wait for it.
 *
 * Exported for unit tests; consumers want `useSectionAccess`.
 */
export function resolveSectionLevel(
  payload: CurrentUserPermissionsData['currentUserPermissions'],
  section: string,
  failed = false,
): SectionAccessLevel | null {
  if (failed) return 'FULL_ACCESS';
  if (!payload) return null;
  // SuperAdmins get an all-access grant with an EMPTY `permissions` list by
  // contract, so this has to come before the lookup.
  if (payload.isSuperAdmin) return 'FULL_ACCESS';
  const entries = payload.permissions ?? [];
  // An authenticated CRM user always holds at least one section grant, so an
  // empty list means the server could not resolve the map (the onboarding and
  // public request paths skip the preload) — not that access was refused.
  if (entries.length === 0) return 'FULL_ACCESS';
  const entry = entries.find((p) => p?.section === section);
  return (entry?.level as SectionAccessLevel | undefined) ?? 'NO_ACCESS';
}

/**
 * Effective access level for one RBAC section code (e.g.
 * `crm.wealthManagement.transactions`), for gating write affordances in the UI.
 *
 * Reads and writes fail in opposite directions on purpose:
 *
 * - `canRead` stays true until the server says otherwise, so a slow or failed
 *   permissions query never blanks a section the user can actually see.
 * - `canWrite` stays FALSE until an answer arrives. DEV-3922 asks for write
 *   controls to be "disabled from the start"; failing open here would flash
 *   them enabled on first paint and let a READ_ONLY user click straight into
 *   an edit form the server then refuses. The wait costs nothing visible — the
 *   query is `cache-first` and resolves alongside the grid's own skeleton, and
 *   later mounts read it from cache with `fetching` already false.
 */
export function useSectionAccess(section: string): SectionAccess {
  const [{ data, fetching, error }] = useQuery<CurrentUserPermissionsData>({
    query: CURRENT_USER_SECTION_ACCESS,
    requestPolicy: 'cache-first',
  });

  const level = useMemo(
    () => resolveSectionLevel(data?.currentUserPermissions, section, Boolean(error)),
    [data, error, section],
  );

  return {
    level,
    canRead: level !== 'NO_ACCESS',
    canWrite: level === 'FULL_ACCESS',
    fetching,
  };
}
